import React, { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import { Text } from '@atlaskit/primitives';
import Form, { FormSection, FormHeader, FormFooter } from '@atlaskit/form';
import {
  useNotification,
  useSidebar,
  NotificationTypes,
  successNotificationBuilder,
  errorNotificationBuilder,
} from '@css-xp/base';
import { AddContactMultiSelectField } from '@css-xp/ppc-add-contact-fields';
import { useFetchBillingContactsFromContactService, useContactServicePayload } from '@css-xp/contact-resources';
import { createContactRelationship } from '@atlassian/temp-proxies';
import { failureMessage } from '@css-xp/ppc-common';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import { BillingAdmin } from '@css-xp/ppc-graphtypes';

interface Props {
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  invoiceGroupId: string;
  transactionAccountBillingAdmins: BillingAdmin[];
  version: number;
}

const successNotificationOptions = successNotificationBuilder()
  .withId('add-billing-profile-billing-contact-success')
  .withHeader('Billing admin added')
  .withDismissAfter(5000)
  .isVersion(NotificationTypes.FLAG)
  .build();

const errorNotificationOptions = errorNotificationBuilder()
  .isVersion(NotificationTypes.FLAG)
  .withHeader(failureMessage())
  .withDismissAfter(5000)
  .build();

export const BillingAdminPane = ({
  invoiceGroupId,
  refetch,
  transactionAccountId,
  transactionAccountBillingAdmins,
  version,
}: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingBillingAdmins, setExistingBillingAdmins] = useState([]);
  const [billingAdmins, setBillingAdmins] = useState<BillingAdmin[]>([]);
  const { closeSidebar } = useSidebar();
  const { getBillingContactDetails, billingContactsLoading } = useFetchBillingContactsFromContactService();

  const existingBillingAdminIds = existingBillingAdmins?.map(({ accountId }) => accountId);

  useEffect(() => {
    (async () => {
      const data = await getBillingContactDetails(invoiceGroupId, transactionAccountId);
      setExistingBillingAdmins(data);
    })();
  }, [invoiceGroupId, transactionAccountId]);

  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const { getCreateContactRelationshipsPayload } = useContactServicePayload();

  const handleFormSubmit = async () => {
    setIsLoading(true);

    try {
      const createCustomerContactPayload = getCreateContactRelationshipsPayload({
        invoiceGroupId,
        transactionAccountId,
        createContactIds: [],
        createBillingContactIds: billingAdmins?.map((contact: BillingAdmin) => contact.atlassianAccountId) as string[],
      });

      await createContactRelationship(createCustomerContactPayload as CreateContactRelationshipsRequest);

      setIsLoading(false);

      if (!notificationSent) {
        sendSuccessNotification(successNotificationOptions);
        setNotificationSent(true);
        closeSidebar();
        refetch();
      }
    } catch (error) {
      setIsLoading(false);
      if (!notificationSent) {
        sendErrorNotification(errorNotificationOptions);
        setNotificationSent(true);
      }
    }
  };

  return (
    <div data-testid="billing-admin-pane">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={version === 2 ? '' : 'Add billing admin'}>
              <Text size="small">
                Billing admin for this billing profile who communicates on behalf of partners — you can update or add
                more later.
              </Text>
            </FormHeader>

            <FormSection>
              <AddContactMultiSelectField
                currentUserList={existingBillingAdminIds}
                emailFieldText="Select from the list"
                fieldType="billing-contact"
                billingAdmins={transactionAccountBillingAdmins}
                billingAdminsLoading={billingContactsLoading}
                setBillingAdmins={setBillingAdmins}
              />
            </FormSection>

            <FormFooter>
              <ButtonGroup>
                <Button onClick={closeSidebar}>Cancel</Button>
                <LoadingButton
                  isDisabled={
                    billingContactsLoading || !billingAdmins?.length || !transactionAccountBillingAdmins?.length
                  }
                  appearance="primary"
                  type="submit"
                  testId="add-billing-admin-button"
                  isLoading={isLoading}
                >
                  Add
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default BillingAdminPane;
